import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ArrowCollapseIcon,
  ArrowExpandIcon,
  CircleInfoIcon,
  XIcon,
} from '@persona-id/design-tokens/icons';
import pluralize from 'pluralize';
import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import useEditorSubPath from 'dashboard/apps/InquiryTemplateEditor/hooks/useEditorSubPath';
import { TOOLBAR_HEIGHT } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import { StyledIconDrag } from 'dashboard/lib/dnd/StyledIconDrag';
import {
  FieldSchemaErrorEntity,
  InquiryTemplateVersionError,
  StepErrorEntity,
  UiComponentErrorEntity,
  WorkflowVersionErrorEntity,
  WorkflowVersionStepErrorEntity,
} from 'dashboard/lib/interfaces/InquiryTemplateVersionErrors';
import { Z_INDICES } from 'dashboard/styles/variables';
import Badge from 'design-system/components/Badge/Badge';
import Button from 'design-system/components/Button';
import { Tab } from 'design-system/components/TabBar';
import TabBar from 'design-system/components/TabBar/TabBar';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

export enum BottomPanelTab {
  Errors = 'errors',
}

export enum BottomPanelSize {
  Maximized = 'maximized', // the whole panel is at max height
  Minimized = 'minimized', // the whole panel is set to height auto, to barely show everything
  Drawer = 'drawer', // the panel height is at an adjustable height, determined by the height state
}

// 32 from the row containing Errors, 4 from top padding, 1 from border size
export const MINIMUM_BOTTOM_PANEL_HEIGHT = 37;
// -2 from the TOOLBAR_HEIGHT so that the border between the toolbar
// and the bottom panel (when maximized) is the correct width
const FLE2_HEADER_HEIGHT = FULL_SCREEN_HEADER_HEIGHT + TOOLBAR_HEIGHT - 2;
const FLE3_HEADER_HEIGHT = FULL_SCREEN_HEADER_HEIGHT;
const SUPPORT_CHAT_POPUP_HEIGHT_OFFSET = 108;

const TriStatePanel = styled.div.attrs<{
  panelSize: BottomPanelSize;
  customHeight: number | null;
  enableLeftNav: boolean;
}>(({ panelSize, customHeight, enableLeftNav }) => ({
  style: {
    height:
      panelSize === BottomPanelSize.Drawer
        ? `${customHeight ? `${customHeight}px` : `${MINIMUM_BOTTOM_PANEL_HEIGHT}px`}`
        : panelSize === BottomPanelSize.Maximized
          ? `calc(100vh - ${enableLeftNav ? FLE3_HEADER_HEIGHT : FLE2_HEADER_HEIGHT}px)`
          : 'auto',
  },
}))<{
  panelSize: BottomPanelSize;
  customHeight: number | null;
  enableLeftNav: boolean;
}>`
  display: flex;
  flex-direction: column;
  background-color: white;
  position: relative;
  min-height: ${MINIMUM_BOTTOM_PANEL_HEIGHT}px;
  padding: ${Spacing[1]} ${Spacing[4]} 0 ${Spacing[4]};
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  z-index: ${Z_INDICES.floatingBar};
  overflow-y: hidden;
`;

const DragHandleContainer = styled.div`
  // offset by padding of TriStatePanel
  width: calc(100% - 32px);
  position: absolute;
  top: 5px;
  text-align: center;
  cursor: row-resize;
`;

interface VerticalDragHandlerProps {
  onMoveTo: (y: number) => void;
  onRelease: () => void;
  onDoubleClick: () => void;
}

const VerticalDragHandler: FC<VerticalDragHandlerProps> = ({
  onMoveTo,
  onRelease,
  onDoubleClick,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const resize = (event: MouseEvent) => {
      if (isDragging) {
        onMoveTo(event.clientY);
      }
    };

    const stopDragging = () => {
      setIsDragging(false);
      onRelease();
    };

    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopDragging);
    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopDragging);
    };
  }, [isDragging, onMoveTo, onRelease]);
  return (
    <DragHandleContainer
      aria-label='Drag to resize bottom panel'
      onMouseDown={(e) => {
        setIsDragging(true);
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseUp={() => {
        setIsDragging(false);
      }}
      onMouseOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDoubleClick={onDoubleClick}
    >
      <StyledIconDrag $active={false} $horizontal={true} width={14} />
    </DragHandleContainer>
  );
};

const TopRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const TopRightIconsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const ContentContainer = styled.div`
  padding-top: ${Spacing[2]};
  // offset by the top row container height
  height: calc(100% - 32px);
`;

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0px ${Spacing[4]};
`;

const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScrollableVerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: scroll;
`;

const IconContainer = styled.div`
  align-content: center;
  padding-right: ${Spacing[2]};
`;

const ValidationMessageListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const LinkContainer = styled(Link)`
  display: flex;
  flex-direction: row;
  cursor: 'pointer';
`;

const ValidationMessageContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${Spacing[2]};
  align-content: center;
`;

interface ValidationMessageListProps {
  validationErrors: InquiryTemplateVersionError[];
}

const ValidationMessageList = ({ validationErrors }: ValidationMessageListProps) => {
  const editorBasePath = useEditorSubPath({ editorSubPath: 'config', onlyBasePath: true });
  const errorsNode = validationErrors.map((error, index) => {
    const firstRelevantEntity = error['relevant-entities']?.[0];
    const linkAndLocationMessage = (() => {
      if (firstRelevantEntity) {
        switch (firstRelevantEntity['entity-type']) {
          case 'ui_component':
            return {
              link: `${editorBasePath}/screens/${(firstRelevantEntity as UiComponentErrorEntity)['step-name']}/${(firstRelevantEntity as UiComponentErrorEntity)['name']}`,
              message: `Component: ${(firstRelevantEntity as UiComponentErrorEntity)['name']}, in Step: ${(firstRelevantEntity as UiComponentErrorEntity)['step-name']}`,
            };
          case 'step':
            return {
              link: `${editorBasePath}/screens/${(firstRelevantEntity as StepErrorEntity)['name']}`,
              message: `Step: ${(firstRelevantEntity as StepErrorEntity)['name']}`,
            };
          case 'workflow_version_step':
            return {
              link: `${editorBasePath}/actions/${(firstRelevantEntity as WorkflowVersionStepErrorEntity)['workflow-version-token']}/${(firstRelevantEntity as WorkflowVersionStepErrorEntity).token}`,
              message: `Action Sequence Step: ${(firstRelevantEntity as WorkflowVersionStepErrorEntity).name}`,
            };
          case 'workflow_version':
            return {
              link: `${editorBasePath}/actions/${(firstRelevantEntity as WorkflowVersionErrorEntity)['token']}`,
              message: 'Action Sequence',
            };
          case 'field_schema':
            return {
              link: `${editorBasePath}/config/fields/${(firstRelevantEntity as FieldSchemaErrorEntity).key}`,
              message: 'Field Schema',
            };
          default:
            return {
              link: `${editorBasePath}`,
              message: 'Configuration',
            };
        }
      }
    })();
    return (
      <ValidationMessageContainer key={`${index}-${error['error-message']}`}>
        <IconContainer>
          <CircleInfoIcon color={Color.CoreOrange600} />
        </IconContainer>
        <VerticalContainer>
          {linkAndLocationMessage != null && (
            <LinkContainer to={linkAndLocationMessage.link}>
              {linkAndLocationMessage.message}
            </LinkContainer>
          )}
          {error['error-message']}
        </VerticalContainer>
      </ValidationMessageContainer>
    );
  });

  return <ValidationMessageListContainer>{errorsNode}</ValidationMessageListContainer>;
};

interface BottomPanelProps {
  validationErrors: InquiryTemplateVersionError[];
}

const BottomPanel = ({ validationErrors }: BottomPanelProps) => {
  const enableLeftNav = useFeatureFlag('dashboard:fle-redesign-left-nav-and-panels');
  const headerHeightOffset = enableLeftNav ? FLE3_HEADER_HEIGHT : FLE2_HEADER_HEIGHT;

  // used to keep track of the panel height when panel size is drawer
  const [height, setHeight] = useState<number | null>(null);
  const [panelSize, setPanelSize] = useState<BottomPanelSize>(BottomPanelSize.Drawer);
  const [selectedTab, setSelectedTab] = useState<BottomPanelTab | null>(BottomPanelTab.Errors);

  const onSelectTab = (tab: BottomPanelTab) => {
    // TODO: Minimized is just height 'auto' and if there are a lot of errors, it may already exceed height 250.
    //        Consider using a ref to get actual height and comparing against that if minimized
    // set height to a minimum of 250
    if (
      (panelSize === BottomPanelSize.Drawer && (height == null || height < 250)) ||
      panelSize === BottomPanelSize.Minimized
    ) {
      setHeight(250);
      setPanelSize(BottomPanelSize.Drawer);
    }
    setSelectedTab(tab);
  };

  return (
    <TriStatePanel
      id='inquiry-template-editor-bottom-panel__panel'
      role='panel'
      panelSize={panelSize}
      customHeight={height}
      enableLeftNav={enableLeftNav}
    >
      <VerticalDragHandler
        onMoveTo={(y) => {
          setPanelSize(BottomPanelSize.Drawer);
          const { innerHeight } = window;
          // The whole thing looks better with 10 px offset
          setHeight(Math.min(innerHeight - y + 10, innerHeight - headerHeightOffset));
        }}
        onRelease={() => {
          if (!height) {
            return;
          }
          const { innerHeight } = window;
          const availableHeight = innerHeight - headerHeightOffset;
          // if the bottom panel is dragged close to the top, set the panel size to maximized to just fill out everything
          // this also causes the top right button to now function as a minimize button
          if (height > availableHeight * 0.95) {
            setPanelSize(BottomPanelSize.Maximized);
            setHeight(innerHeight);
          } else if (height < MINIMUM_BOTTOM_PANEL_HEIGHT) {
            setPanelSize(BottomPanelSize.Drawer);
            setHeight(MINIMUM_BOTTOM_PANEL_HEIGHT);
          }
        }}
        onDoubleClick={() => {
          if (panelSize === BottomPanelSize.Maximized) {
            setPanelSize(BottomPanelSize.Drawer);
            setHeight(null);
          } else {
            setPanelSize(BottomPanelSize.Maximized);
            setHeight(window.innerHeight);
          }
        }}
      />
      <TopRowContainer>
        <HorizontalContainer>
          <TabBar>
            <Tab
              id='bottom-panel__tab-errors'
              aria-label='Bottom panel tab for errors'
              selected={selectedTab === BottomPanelTab.Errors}
              onClick={() => onSelectTab(BottomPanelTab.Errors)}
            >
              Errors
              <Badge
                variant={selectedTab === BottomPanelTab.Errors ? 'alert' : 'basic'}
                css={{ marginLeft: Spacing[1] }}
                aria-label={`${validationErrors.length} ${pluralize('error', validationErrors.length)} found`}
              >
                {validationErrors.length}
              </Badge>
            </Tab>
          </TabBar>
        </HorizontalContainer>
        {((height != null && height >= SUPPORT_CHAT_POPUP_HEIGHT_OFFSET) ||
          panelSize !== BottomPanelSize.Drawer) && (
          <TopRightIconsContainer>
            {(panelSize === BottomPanelSize.Drawer || panelSize === BottomPanelSize.Minimized) && (
              <Button
                id='inquiry-template-editor-bottom-panel__maximize-button'
                aria-label='Maximize the flow editor bottom panel'
                width='icon'
                variant='inverse'
                onClick={() => {
                  setPanelSize(BottomPanelSize.Maximized);
                  // select the first tab if no tab is selected
                  if (selectedTab == null) {
                    setSelectedTab(BottomPanelTab.Errors);
                  }
                }}
              >
                <ArrowExpandIcon />
              </Button>
            )}
            {panelSize === BottomPanelSize.Maximized && (
              <Button
                id='inquiry-template-editor-bottom-panel__maximize-button'
                aria-label='Minimize the flow editor bottom panel'
                width='icon'
                variant='inverse'
                onClick={() => {
                  setPanelSize(BottomPanelSize.Minimized);
                }}
              >
                <ArrowCollapseIcon />
              </Button>
            )}
            <Button
              id='inquiry-template-editor-bottom-panel__close-button'
              aria-label='Close the flow editor bottom panel'
              width='icon'
              variant='inverse'
              onClick={() => {
                setPanelSize(BottomPanelSize.Drawer);
                setHeight(null);
              }}
            >
              <XIcon />
            </Button>
          </TopRightIconsContainer>
        )}
      </TopRowContainer>
      {selectedTab === BottomPanelTab.Errors && (
        <ContentContainer>
          <ScrollableVerticalContainer>
            <ValidationMessageList validationErrors={validationErrors} />
          </ScrollableVerticalContainer>
        </ContentContainer>
      )}
    </TriStatePanel>
  );
};

export default BottomPanel;
